import {ActionBar as PrimerActionBar} from '@primer/react'
import type {
  ComponentProps,
  ElementType,
  HTMLAttributes,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  ReactNode,
  Ref,
  RefObject,
} from 'react'
import {useEffect, useRef} from 'react'

const inlineAnchorAttribute = 'data-action-bar-picker-anchor'
const overflowAnchorSelector = ':scope > [role="toolbar"] > button[data-component="IconButton"]'

export type ActionBarButtonAnchorOptions = Omit<
  ComponentProps<typeof PrimerActionBar.Button>,
  'children' | 'onClick' | 'onKeyDown' | 'ref'
> & {
  as?: ElementType
  children: ReactNode
}

export type AnchorRenderProps = HTMLAttributes<HTMLElement> & {ref?: Ref<HTMLButtonElement>}

export type ActionBarButtonAnchorRenderer = (
  props: AnchorRenderProps,
  ref?: RefObject<HTMLButtonElement | null>,
) => ReactElement

function getRefValue(ref: Ref<HTMLButtonElement> | undefined) {
  return typeof ref === 'object' && ref ? ref.current : null
}

function setRefValue(ref: Ref<HTMLButtonElement> | undefined, value: HTMLButtonElement | null) {
  if (typeof ref === 'function') {
    ref(value)
  } else if (ref) {
    ref.current = value
  }
}

function getAnchorRefs(...refs: Array<Ref<HTMLButtonElement> | undefined>) {
  return refs.filter((ref, index): ref is Ref<HTMLButtonElement> => Boolean(ref) && refs.indexOf(ref) === index)
}

interface OverflowAnchorAttributesSnapshot {
  element: HTMLButtonElement
  ariaExpanded: string | null
  ariaHasPopup: string | null
}

export type ActionBarButtonAnchorProps = ActionBarButtonAnchorOptions &
  AnchorRenderProps & {
    anchorRef?: RefObject<HTMLButtonElement | null>
  }

export function ActionBarButtonAnchor({
  anchorRef,
  as: ButtonComponent = PrimerActionBar.Button,
  children,
  onClick,
  onKeyDown,
  ref: renderAnchorRef,
  ...buttonProps
}: ActionBarButtonAnchorProps) {
  const visibleAnchorRef = useRef<HTMLButtonElement | null>(null)
  const overflowAnchorAttributesRef = useRef<OverflowAnchorAttributesSnapshot | null>(null)
  const open = buttonProps['aria-expanded'] === true
  const wasOpenRef = useRef(open)

  const restoreOverflowAnchorAttributes = () => {
    const snapshot = overflowAnchorAttributesRef.current
    if (!snapshot) return

    if (snapshot.ariaExpanded === null) {
      snapshot.element.removeAttribute('aria-expanded')
    } else {
      snapshot.element.setAttribute('aria-expanded', snapshot.ariaExpanded)
    }

    if (snapshot.ariaHasPopup === null) {
      snapshot.element.removeAttribute('aria-haspopup')
    } else {
      snapshot.element.setAttribute('aria-haspopup', snapshot.ariaHasPopup)
    }

    overflowAnchorAttributesRef.current = null
  }

  const syncOverflowAnchorAttributes = (inlineAnchor: HTMLButtonElement, visibleAnchor: HTMLButtonElement) => {
    if (overflowAnchorAttributesRef.current?.element !== visibleAnchor) restoreOverflowAnchorAttributes()

    const ariaExpanded = inlineAnchor.getAttribute('aria-expanded')
    if (visibleAnchor === inlineAnchor || ariaExpanded !== 'true') {
      restoreOverflowAnchorAttributes()
      return
    }

    if (!overflowAnchorAttributesRef.current) {
      overflowAnchorAttributesRef.current = {
        element: visibleAnchor,
        ariaExpanded: visibleAnchor.getAttribute('aria-expanded'),
        ariaHasPopup: visibleAnchor.getAttribute('aria-haspopup'),
      }
    }

    // Mirror popup state to the visible button used by assistive technology.
    if (visibleAnchor.getAttribute('aria-expanded') !== ariaExpanded) {
      visibleAnchor.setAttribute('aria-expanded', ariaExpanded)
    }

    const ariaHasPopup = inlineAnchor.getAttribute('aria-haspopup')
    if (ariaHasPopup !== null && visibleAnchor.getAttribute('aria-haspopup') !== ariaHasPopup) {
      visibleAnchor.setAttribute('aria-haspopup', ariaHasPopup)
    }
  }

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- Primer snapshots return focus when the overlay opens, before responsive overflow can change the visible trigger.
    if (wasOpenRef.current && !open) visibleAnchorRef.current?.focus()
    wasOpenRef.current = open
  }, [open])

  const setInlineAnchor = (element: HTMLButtonElement | null) => {
    const anchorRefs = getAnchorRefs(anchorRef, renderAnchorRef)
    if (!element) {
      restoreOverflowAnchorAttributes()
      visibleAnchorRef.current = null
      for (const ref of anchorRefs) setRefValue(ref, null)
      return
    }

    const actionBar = element.closest('[data-component="ActionBar"]')
    const syncAnchor = () => {
      const overflowAnchor = actionBar?.querySelector<HTMLButtonElement>(overflowAnchorSelector)
      const nextAnchor = element.hasAttribute('data-overflowing') ? (overflowAnchor ?? element) : element
      visibleAnchorRef.current = nextAnchor
      for (const ref of anchorRefs) setRefValue(ref, nextAnchor)
      syncOverflowAnchorAttributes(element, nextAnchor)
    }

    syncAnchor()
    if (!actionBar) return

    const observer = new MutationObserver(syncAnchor)
    observer.observe(actionBar, {
      attributes: true,
      attributeFilter: ['aria-expanded', 'aria-haspopup', 'data-overflowing', 'data-more-button-inactive'],
      subtree: true,
    })

    return () => {
      observer.disconnect()
      restoreOverflowAnchorAttributes()
      visibleAnchorRef.current = null
      for (const ref of anchorRefs) setRefValue(ref, null)
    }
  }

  const handleClick = (event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => {
    const anchorRefs = getAnchorRefs(anchorRef, renderAnchorRef)
    const currentAnchor = anchorRefs.map(getRefValue).find(Boolean)
    const actionBar = currentAnchor?.closest('[data-component="ActionBar"]')
    const nextAnchor = event.currentTarget.hasAttribute(inlineAnchorAttribute)
      ? (event.currentTarget as HTMLButtonElement)
      : (actionBar?.querySelector<HTMLButtonElement>(overflowAnchorSelector) ?? currentAnchor ?? null)
    visibleAnchorRef.current = nextAnchor
    for (const ref of anchorRefs) setRefValue(ref, nextAnchor)

    if ('key' in event) queueMicrotask(() => onKeyDown?.(event))
    else onClick?.(event)
  }

  return (
    <ButtonComponent
      {...buttonProps}
      ref={setInlineAnchor}
      data-action-bar-picker-anchor=""
      onClick={handleClick}
      onKeyDown={onKeyDown}
    >
      {children}
    </ButtonComponent>
  )
}

export function createActionBarButtonAnchor(options: ActionBarButtonAnchorOptions) {
  return function ActionBarButtonAnchorRenderer(
    anchorProps: AnchorRenderProps,
    anchorRef?: RefObject<HTMLButtonElement | null>,
  ) {
    const {children, ...buttonProps} = options
    return (
      <ActionBarButtonAnchor {...buttonProps} {...anchorProps} anchorRef={anchorRef}>
        {children}
      </ActionBarButtonAnchor>
    )
  }
}
